import { Behaviour } from "@iunctim/utility-frontend/Resources/Public/Scripts/utilities/ResponsiveBehaviour.js";
import { FocusTrap } from "@iunctim/component-overlay/Resources/Public/Scripts/features/FocusTrap.js";
import {
    Debounce,
    DebounceWithCancel,
} from "@iunctim/utility-frontend/Resources/Public/Scripts/utilities/Debounce.js";
import { DeepRequired } from "@iunctim/utility-frontend/Resources/Public/Scripts/utilities/DeepRequired.js";

/**
 * Helpers to define possible configuration
 * options for the dropdown-menu
 */
export interface DropdownOptions {
    hover?: boolean;
    debounce: {
        enter?: number;
        leave?: number;
    };
}

type DropdownOptionsWithFallbacks = DeepRequired<DropdownOptions>;

/**
 * Multi-Level-Dropdown behaviour for the
 * composition within responsive menus
 */
export class Dropdown implements Behaviour {
    private menu: HTMLElement;
    private levels: Level[];
    private _options: DropdownOptionsWithFallbacks;

    public ignoreHoverLeave: boolean = false;

    /**
     * Constructor of the Dropdown
     *
     * @param {HTMLElement} scope
     * @param {DropdownOptions} options
     */
    public constructor(scope: HTMLElement, options: DropdownOptions) {
        this._options = {
            hover: options.hover ?? true,
            debounce: {
                enter: options.debounce?.enter ?? 75,
                leave: options.debounce?.leave ?? 25,
            },
        };

        const selector = `[scope="header-main-menu-behaviour"][template="dropdown"]`;
        this.menu = scope.querySelector<HTMLElement>(selector)!;

        this.levels = this.levelsOf(this.menu);
    }

    /**
     * Method to access the options
     *
     * @returns {DropdownOptionsWithFallbacks}
     */
    public get options(): DropdownOptionsWithFallbacks {
        return this._options;
    }

    /**
     * Selects the direct submenus of an element
     *
     * @param {HTMLElement | null} scope
     * @param {Set<Level> | null} [ancestors=null]
     * @returns {Level[]}
     */
    private levelsOf(
        scope: HTMLElement | null,
        ancestors: Set<Level> | null = null,
    ): Level[] {
        if (scope && !(scope instanceof HTMLOListElement)) {
            scope = scope.querySelector("ol:first-child");
        }
        if (!scope) return [];

        const selector = `:scope > li > [aria-haspopup="true"]`;
        const clickTargets = scope.querySelectorAll<HTMLElement>(selector); // svg

        return Array.from(clickTargets)
            .flatMap((clickTarget) => {
                const hoverTarget = clickTarget.parentElement; // li
                if (hoverTarget) {
                    const menu =
                        hoverTarget.querySelector<HTMLElement>(`[role="menu"]`); // ol
                    if (menu) {
                        const _ancestors = ancestors
                            ? new Set(ancestors)
                            : new Set<Level>();
                        const level = new Level(
                            this,
                            menu,
                            clickTarget,
                            hoverTarget,
                            _ancestors,
                        );

                        _ancestors.add(level);
                        return [level, ...this.levelsOf(menu, _ancestors)];
                    }
                }
            })
            .filter((level): level is Level => !!level);
    }

    /**
     * Method to apply the functionality
     *
     * @returns {void}
     */
    public apply(): void {
        this.levels.forEach((level) => level.apply());
        document.addEventListener("click", this.handleDocumentClick);
    }

    /**
     * Method to destroy the functionality
     *
     * @returns {void}
     */
    public destroy(): void {
        this.levels.forEach((level) => level.destroy());
        document.removeEventListener("click", this.handleDocumentClick);
    }

    /**
     * Method to close all submenus
     * except for any referenced level
     *
     * @param {Level | null} [except=null]
     * @returns {void}
     */
    public close(except: Level | null = null): void {
        this.levels.forEach((level) => {
            if (!except || !except.ancestors.has(level)) {
                level.close();
            }
        });
    }

    /**
     * Method to handle the click outside the menu to close it
     *
     * @param {MouseEvent} event
     * @returns {void}
     */
    private handleDocumentClick = (event: MouseEvent): void => {
        const target = event.target as HTMLElement;
        if (this.menu && !this.menu.contains(target)) {
            this.close();
        }
    };
}

/**
 * Single dropdown-level to abstract different
 * levels within the dropdown-behaviour
 */
class Level implements Behaviour {
    // Focus-Trap for the level
    private focusTrap: FocusTrap;

    // Debounced open and close methods
    private debouncedOpen: DebounceWithCancel<() => void>;
    private debouncedClose: DebounceWithCancel<() => void>;

    // Handlers for event-listeners
    private handleHoverEnter: () => void;
    private handleHoverLeave: () => void;
    private handleClick: (event: MouseEvent) => void;
    private handleKeydown: (event: KeyboardEvent) => void;
    private handleTransition: () => void;

    // Event names in use
    private static readonly HOVER_EVENTS = {
        ENTER: "mouseenter",
        LEAVE: "mouseleave",
    } as const;

    private static readonly CLICK_EVENT = "click" as const;
    private static readonly KEYDOWN_EVENT = "keydown" as const;
    private static readonly TRANSITIONED_EVENT = "transitionend" as const;

    /**
     * Constructor of the Level
     *
     * @param {Dropdown} dropdown
     * @param {HTMLElement} menu
     * @param {HTMLElement} clickTarget
     * @param {HTMLElement} hoverTarget
     * @param {Set<Level>} ancestors
     */
    public constructor(
        private dropdown: Dropdown,
        private menu: HTMLElement,
        private clickTarget: HTMLElement,
        private hoverTarget: HTMLElement,
        public ancestors: Set<Level>,
    ) {
        // Instantiate the focus-trap
        this.focusTrap = new FocusTrap(menu);

        // Define debounced open and close methods
        this.debouncedOpen = Debounce.cancellable(
            () => this.open(),
            this.dropdown.options.debounce.enter,
        );
        this.debouncedClose = Debounce.cancellable(
            () => this.close(),
            this.dropdown.options.debounce.leave,
        );

        // Define handlers for event-listeners to
        // preserve the context for later removal
        this.handleHoverEnter = () => {
            this.debouncedClose.cancel();
            this.debouncedOpen();
        };
        this.handleHoverLeave = () => {
            if (!this.dropdown.ignoreHoverLeave) {
                this.debouncedOpen.cancel();
                this.debouncedClose();
            }
        };
        this.handleClick = (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.toggle();
        };
        this.handleTransition = () => {
            if (!this.menu.hidden) {
                this.position();
            }
        };
        this.handleKeydown = (event: KeyboardEvent) => {
            switch (event.key) {
                case "ArrowRight":
                case "ArrowDown":
                    if (
                        event.target === this.clickTarget ||
                        event.currentTarget === this.hoverTarget
                    ) {
                        event.preventDefault();
                        event.stopPropagation();
                        this.open(true);
                    }
                    break;
                case "Enter":
                    if (event.target === this.clickTarget) {
                        event.preventDefault();
                        event.stopPropagation();
                        this.open(true);
                    }
                    break;
                case "ArrowLeft":
                case "ArrowUp":
                case "Escape":
                    if (this.menu.contains(event.target as Node)) {
                        event.preventDefault();
                        event.stopPropagation();

                        this.dropdown.ignoreHoverLeave = true;
                        this.close(true);

                        setTimeout(
                            () => (this.dropdown.ignoreHoverLeave = false),
                            this.dropdown.options.debounce.leave + 50,
                        );
                    }
                    break;
            }
        };
    }

    /**
     * Method to apply the functionality
     *
     * @returns {void}
     */
    public apply(): void {
        // Setup event-listeners for hovering
        if (this.dropdown.options.hover) {
            this.hoverTarget.addEventListener(
                Level.HOVER_EVENTS.ENTER,
                this.handleHoverEnter,
            );
            this.hoverTarget.addEventListener(
                Level.HOVER_EVENTS.LEAVE,
                this.handleHoverLeave,
            );
        }

        // Allow toggling via click on all
        // for proper accessibility
        this.clickTarget.addEventListener(Level.CLICK_EVENT, this.handleClick);

        // Allow toggling via keydown
        // for proper accessibility
        this.hoverTarget.addEventListener(
            Level.KEYDOWN_EVENT,
            this.handleKeydown,
        );
        this.clickTarget.addEventListener(
            Level.KEYDOWN_EVENT,
            this.handleKeydown,
        );
        this.menu.addEventListener(Level.KEYDOWN_EVENT, this.handleKeydown);

        // Position the level when opened
        this.menu.addEventListener(
            Level.TRANSITIONED_EVENT,
            this.handleTransition,
        );
    }

    /**
     * Method to destroy the functionality
     *
     * @returns {void}
     */
    public destroy(): void {
        // Remove the event-listeners
        this.hoverTarget.removeEventListener(
            Level.HOVER_EVENTS.ENTER,
            this.handleHoverEnter,
        );
        this.hoverTarget.removeEventListener(
            Level.HOVER_EVENTS.LEAVE,
            this.handleHoverLeave,
        );
        this.hoverTarget.removeEventListener(
            Level.KEYDOWN_EVENT,
            this.handleKeydown,
        );
        this.clickTarget.removeEventListener(
            Level.CLICK_EVENT,
            this.handleClick,
        );
        this.clickTarget.removeEventListener(
            Level.KEYDOWN_EVENT,
            this.handleKeydown,
        );
        this.menu.removeEventListener(Level.KEYDOWN_EVENT, this.handleKeydown);
        this.menu.removeEventListener(
            Level.TRANSITIONED_EVENT,
            this.handleTransition,
        );

        // Cancel all debounded actions
        this.debouncedOpen.cancel();
        this.debouncedClose.cancel();

        // Set aria-expanded to false
        this.clickTarget.setAttribute("aria-expanded", "false");

        // Set the hidden attribute to true
        this.menu.hidden = true;

        // Remove any added classes
        this.removeClasses();
    }

    /**
     * Method to open the level
     *
     * @param {boolean} [useFocusTrap=false]
     * @returns {void}
     */
    private open(useFocusTrap: boolean = false): void {
        if (this.menu.hidden) {
            this.dropdown.close(this);
            this.clickTarget.setAttribute("aria-expanded", "true");
            this.menu.hidden = false;

            this.position();

            if (useFocusTrap) {
                this.focusTrap.enable(this.clickTarget);
            } else {
                this.focusTrap.setInitial(this.clickTarget);
            }
        }
    }

    /**
     * Method to close the level
     *
     * @param {boolean} [refocus=false]
     * @returns {void}
     */
    public close(refocus: boolean = false): void {
        if (!this.menu.hidden) {
            this.menu.hidden = true;
            this.clickTarget.setAttribute("aria-expanded", "false");

            this.focusTrap.disable(refocus);
        }
    }

    /**
     * Method to toggle the level
     *
     * @returns {void}
     */
    private toggle(): void {
        if (this.menu.hidden) {
            this.open();
        } else {
            this.close();
        }
    }

    /**
     * Method to position a menu relative to it's parent
     * element based on it's position within the viewport
     * and the orientation of the items within the menu
     *
     * @returns {void}
     */
    private position(): void {
        // Reset to default
        this.removeClasses();

        // Calculation context
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // Positioning based on the orientation of the parent menu
        const parentMenu = this.menu.parentElement?.closest(`[role="menu"]`);
        switch (parentMenu?.getAttribute("aria-orientation")) {
            case "horizontal": {
                // Set to default
                this.menu.classList.add("left-0");
                this.menu.classList.add("top-full");

                // Additional calculation context
                const rect = this.menu.getBoundingClientRect();

                // Horizontal positioning
                if (rect.right > viewportWidth) {
                    this.menu.classList.add("right-0");
                    this.menu.classList.remove("left-0");
                }

                // Vertical positioning
                if (rect.bottom > viewportHeight) {
                    this.menu.classList.add("bottom-full");
                    this.menu.classList.remove("top-full");
                }
                break;
            }
            case "vertical": {
                // Set to default
                this.menu.classList.add("left-full");
                this.menu.classList.add("top-dm");

                // Additional calculation context
                const rect = this.menu.getBoundingClientRect();

                // Horizontal positioning
                if (rect.right > viewportWidth) {
                    this.menu.classList.add("right-full");
                    this.menu.classList.remove("left-full");
                }

                // Vertical positioning
                if (rect.bottom > viewportHeight) {
                    this.menu.classList.add("bottom-dm");
                    this.menu.classList.remove("top-dm");
                }
                break;
            }
        }
    }

    /**
     * Method to reset all added classes
     *
     * @returns {void}
     */
    public removeClasses(): void {
        ["top", "left", "bottom", "right"].forEach((position) => {
            this.menu.classList.remove(`${position}-0`);
            this.menu.classList.remove(`${position}-dm`);
            this.menu.classList.remove(`${position}-full`);
        });
    }
}
